import React, { FunctionComponent, useState, useRef, useEffect, CSSProperties, useMemo } from 'react';
import {
  Button,
  Col,
  Container,
  FormCheck,
  FormControl,
  Modal,
  Row,
  DropdownButton,
  Dropdown,
} from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import { Form } from 'react-final-form';
import { connect, ConnectedProps } from 'react-redux';
import apis from '../apis';
import {
  AuthedLayout,
  EditableTableDiv,
  HeaderDisplayKeysWithType,
  DateTimeField,
  DateTimePicker
} from '../components';
import { LabSem, SchoolRank, ClassSetting } from '../model';
import { ErrorDispatches } from '../redux/Dispatches';
import { ApplicationState } from '../redux/States';
import { DateTime } from 'luxon';
import { I18N } from '../i18n-raw';
import { getPhysicalGradeByRank } from '../utils/grade';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';

interface Grades {
  [key: string]: string;
}
interface LabKinds {
  [key: string]: string;
}
const labKinds: LabKinds = { '1': '寄生蟲', '2': '尿液' };
const modalDisplayHeader: HeaderDisplayKeysWithType<LabSem>[] = [
  { property: 'year', display: '受檢學年' },
  { property: 'gradeID', display: '檢查年級' },
  { property: 'no', display: '檢查班級' },
  {
    property: 'labKind',
    display: '檢查種類',
    onRender(value: unknown, idx, editing, context) {
      if (value) {
        return typeof value == 'number' ? labKinds[value] : '無檢查項目';
      }
    },
  },
  {
    property: 'checkDate',
    display: '初查日期',
    onRender: (v: unknown) => {
      return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : null;
    },
  },
  {
    property: 'checkDate2',
    display: '複查日期',
    onRender: (v: unknown) => {
      return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : null;
    },
  },
  { 
    property: 'isSynced', 
    display: '更新至學生資料',
    onRender: (v, i, e, c) => {
      const syncDate =  c.allValues.syncDate instanceof DateTime ? c.allValues.syncDate.toFormat('yyyy/MM/dd HH:mm:ss') : null;
      return v ? "已更新\n" + syncDate : "尚未更新";
    },
  }
];

const mapState = (state: ApplicationState) => ({
  ...state.auth,
});
const dispatches = ErrorDispatches;
const connector = connect(mapState, dispatches);
const dashBorder: CSSProperties = {
  borderTopWidth: '2px',
  borderTopColor: 'gray',
  borderTopStyle: 'dashed',
};

type Props = ConnectedProps<typeof connector>;
interface CheckedClasses {
  [key: string]: boolean;
}
const labSemPage: FunctionComponent<Props> = ({
  user,
  loading,
  catchErrorForModal,
}) => {
  if (loading) {
    useHistory().go(0);
    return <></>;
  }
  const [editing, setEditing] = useState(false);
  const [editingStudent, setEditingStudent] = useState<LabSem>();
  const showCurrentRef = useRef<HTMLInputElement>(null);
  const fromInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);
  const [grade, setGrade] = useState<string>('');
  const [labKind, setLabKind] = useState<number>();
  const [labKindName, setLabKindName] = useState<string>('');
  const [btnDisabled, setBtnDisabled] = useState<boolean>(true);
  const { year, sem } = user.semesters[user.currentSemester];
  const currentSchool = user.currentSchool;
  const physicalGradeRange = getPhysicalGradeByRank(
    user.schools[user.currentSchool].rank ?? SchoolRank.Junior
  );
  const [checkDate, setCheckDate] = useState(DateTime.now()); // 初查日期
  const [checkDate2, setCheckDate2] = useState(DateTime.now()); // 複查日期
  const [labSemList, setLabSemList] = useState([] as LabSem[]);
  const showLabSemNow = useRef<HTMLInputElement>(null);
  const checkDateInputRef = useRef<HTMLInputElement>(null);
  const checkDate2InputRef = useRef<HTMLInputElement>(null);
  const [classSettings, setClassSettings] = useState<ClassSetting[]>([]);
  const [checkedClasses, setCheckedClasses] = useState<CheckedClasses>({});
  const [selectedIndex, setSelectedIndex] = useState<Set<number>>(
    new Set<number>()
  );
  const [updateStudent, setUpdateStudent] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const labSettingData = useMemo(refreshSettings, [
    selectedIndex,
    labSemList,
  ]);

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    const newCheckedClasses: CheckedClasses = {};
    classSettings.forEach((item) => {
      newCheckedClasses[item.id] = checked;
    });
    setCheckedClasses(newCheckedClasses);
  };
  const handleClassCheckChange = (id: string, checked: boolean) => {
    setCheckedClasses((prevState) => ({
      ...prevState,
      [id]: checked,
    }));
    // 如果有一個 checkbox 被取消勾選，全選也應取消
    if (!checked) {
      setSelectAll(false);
    } else {
      const allChecked = classSettings.every(
        (item) => checkedClasses[item.id] || item.id === id
      );
      if (allChecked) {
        setSelectAll(true);
      }
    }
  };

  useEffect(() => {
    getLabSemList();
  }, [year]);

  useEffect(() => {
    setBtnDisabled(true);
    if (checkedClasses) {
      if (labKind) {
        if (checkDate) {
          if (checkDate2) {
            setBtnDisabled(false);
          }
        }
      }
    }
  }, [checkedClasses, labKind]);

  useEffect(() => {
    setUpdateStudent(!selectedIndex.size);
  }, [selectedIndex]);

  function refreshSettings() {
    return labSemList.map((s, i) => ({
      ...s,
      selected: selectedIndex.has(i),
      classId: classSettings.find((c) => c.grade === s.gradeID && c.no === s.no)
        ?.id,
    }));
  }

  // 實驗室檢查設定清單
  async function getLabSemList() {
    const classes = await apis.getClassSettings(year, sem);
    const newSettings = classes
      .filter(e => physicalGradeRange.includes(e.grade))
      .map(e => ({ ...e, className: e.name }));
    setSelectedIndex(new Set()); 
    // setSelectedIndex(new Set(newSettings.map((_, i) => i))); // 預設全選
    setClassSettings(classes.filter((c) => physicalGradeRange.includes(c.grade))
    );
    // if (showLabSemNow.current?.checked) {
      apis
        .getLabSemSettingsNow(year)
        .then((s) => {
          if (s.length) {
            setLabSemList(s.map((n, i) => ({
              ...n,
              selected: selectedIndex.has(i),
              classId: classSettings.find((c) => c.grade === n.gradeID && c.no === n.no)
                ?.id,
            })));
          }
        })
        .catch(catchErrorForModal);
    // } else {
    //   apis
    //     .getLabSemSetting()
    //     .then((s) => {
    //       if (s.length) {
    //         setLabSemList(s.map((n, i) => ({
    //           ...n,
    //           selected: selectedIndex.has(i),
    //           classId: classSettings.find((c) => c.grade === n.gradeID && c.no === n.no)?.id,
    //         })));
    //       }
    //     })
    //     .catch(catchErrorForModal);
    // }
  }

  function insertStudentLabSem() {  
    if (labKind) {
      const classes = classSettings.filter((item) => checkedClasses[item.id]); // 勾選班級
      const classIds = classes.map((x) => x.id);
      const data = classes.map((x) => {
        return {
          year: year,
          gradeID: x.grade,
          no: x.no,
          labKind: labKind,
          checkDate: checkDate,
          checkDate2: checkDate2,
          isSynced: true,
          SyncDate: DateTime.now(),
          schoolId: currentSchool
        };
      });
      toast
        .promise(
          apis.insertLabSemSettingList(data, year, sem, classIds),
          {
            pending: '資料新增中......',
            success: '新增成功！',
            error: '新增失敗！請查看錯誤資訊。',
          }
        )
        .then(() => {
          getLabSemList();
        })
        .catch(catchErrorForModal);
    }
  }

  // 檢查是否重複新增實驗室檢查
  function checkDuplicate() {
    setBtnDisabled(true);
    const isDuplicate = labSemList.some(
      (s) =>
        s.year == year && s.labKind == labKind && s.gradeID == parseInt(grade)
    );

    if (isDuplicate) {
      toast.error(`${year}年${labKindName}實驗室檢查設定已存在`);
    } else {
      insertStudentLabSem();
    }
    setBtnDisabled(false);

  }

  function onHide() {
    setEditing(false);
    setEditingStudent(undefined);
  }

  function onEditMetric(metric: LabSem) {
    setEditing(true);
    setEditingStudent(metric);
  }

  function onHideEditing() {
    setEditing(false);
    setEditingStudent(undefined);
  }

  function onUpdateLabSemExamDate(values: LabSem) {
    updateExamDate(
      values,
      values.checkDate?.toString(),
      values.checkDate2?.toString()
    );
  }

  function clearCheckDate2() {
    if (!editingStudent) return;

    updateExamDate(
      editingStudent,
      editingStudent.checkDate?.toString(),
      null // 清空複查日
    );
  }

  function updateExamDate(
    student: LabSem,
    checkDate?: string,
    checkDate2?: string | null
  ) {
    return toast
      .promise(
        apis.updateLabSemExamDate(
          student.id || 0,
          student.year,
          student.gradeID,
          student.no || 0,
          student.labKind,
          checkDate ?? '',
          checkDate2 ?? null,
          student.schoolId || '',
          DateTime.now().toString(),
          false,
        ),
        {
          pending: '實驗室檢查設定編輯中......',
          success: '實驗室檢查設定上傳成功！',
          error: '實驗室檢查設定上傳失敗！請查看錯誤資訊。',
        }
      )
      .then(() => {
        onHide();
        getLabSemList();
      })
      .catch(catchErrorForModal);
  }

  function modalInputFieldTitle(title: string, require: boolean = false) {
    return (
      <Col sm={2}>
        {require ? <span className="text-danger">*</span> : ''}
        {title}:
      </Col>
    );
  }

  function onDeleteMetric(metric: LabSem) {
    toast
      .promise(
        apis.deleteLabSemSettings(
          metric.id || 0,
          metric.year,
          metric.gradeID,
          metric.labKind,
          metric.checkDate ? metric.checkDate.toString() : '',
          metric.checkDate2 ? metric.checkDate2.toString() : null,
          metric.schoolId || ''
        ),
        {
          pending: '實驗室檢查設定刪除中......',
          success: '實驗室檢查設定刪除成功！',
          error: '實驗室檢查設定刪除失敗！請查看錯誤資訊。',
        }
      )
      .then(() => {
        getLabSemList();
      })
      .catch();
  }

// 更新有受檢且無異狀
function onUpdateLabSemResult() {
  const updateSetting = labSettingData
    .filter((s) => s.selected)
    .map((n, i) => ({...n, isSynced: true, syncDate: DateTime.now()}));
  const classIds = updateSetting
    .map((s) => s.classId)
    .filter((id): id is string => typeof id === 'string');
  if(updateSetting.length && classIds.length) {
    toast
      .promise(apis.updateLabSemResult(updateSetting, classIds, year,  1), {
        pending: '實驗室檢查設定編輯中......',
        success: '實驗室檢查設定上傳成功！',
        error: '實驗室檢查設定上傳失敗！請查看錯誤資訊。',
      })
      .then(() => {
        getLabSemList();
      })
      .catch();
  }
}

// 更新未受檢
function onUpdateLabSemResultNoExmined() {
  const updateSetting = labSettingData
    .filter((s) => s.selected)
    .map((n, i) => ({...n, isSynced: true, syncDate: DateTime.now()}));
    // .map((n, i) => ({...n, isSynced: true, checkDate: DateTime.fromJSDate(checkDate), checkDate2: DateTime.fromJSDate(checkDate2), syncDate: DateTime.now()}));
  const classIds = updateSetting
    .map((s) => s.classId)
    .filter((id): id is string => typeof id === 'string');
  if(updateSetting.length && classIds.length) {
    toast
      .promise(apis.updateLabSemResultNoExmined(updateSetting, classIds, year,  1), {
        pending: '實驗室檢查設定編輯中......',
        success: '實驗室檢查設定上傳成功！',
        error: '實驗室檢查設定上傳失敗！請查看錯誤資訊。',
      })
      .then(() => {
        getLabSemList();
      })
      .catch();
  }
}

// 更新初/複查日期
function onUpdateLabSemResultCheckDate(setCheckDate: boolean, setCheckDate2: boolean) {
  const updateSetting = labSettingData
    .filter((s) => s.selected)
    // .map((n, i) => ({...n, checkDate: DateTime.fromJSDate(checkDate), checkDate2: DateTime.fromJSDate(checkDate2), isSynced: true, syncDate: DateTime.now()}));
    .map((n, i) => ({...n, isSynced: true, syncDate: DateTime.now()}));
    const classIds = updateSetting
    .map((s) => s.classId)
    .filter((id): id is string => typeof id === 'string');
  if(updateSetting.length && classIds.length) {
    toast
      .promise(apis.updateLabSemResultCheckDate(updateSetting, classIds, year,  1, setCheckDate, setCheckDate2), {
        pending: '實驗室檢查設定編輯中......',
        success: '實驗室檢查設定上傳成功！',
        error: '實驗室檢查設定上傳失敗！請查看錯誤資訊。',
      })
      .then(() => {
        getLabSemList();
      })
      .catch();
  }
}

  return (
    <AuthedLayout>
      <Row className="mb-3">
        班級：
        <Col className="text-left">
          <FormCheck
            key="allClass"
            checked={selectAll}
            label="全選"
            onChange={(e) =>
              handleSelectAll(e.currentTarget.checked)
            }
          />
          <div className="d-flex">
            {classSettings.map((item) => (
              <FormCheck
                className="mr-2"
                key={item.id}
                checked={checkedClasses[item.id] || false}
                label={`${item.grade}年${item.name}班`}
                onChange={(e) => {
                  handleClassCheckChange(
                    item.id,
                    e.currentTarget.checked
                  );
                }}
              />
            ))}
          </div>
        </Col>
      </Row>
      <Row>
        <Col sm={2} className="d-flex align-items-center">
          項目：
          <DropdownButton
            className=""
            title={labKindName || '請選擇'}
            onSelect={(k: string | null) => {
              if (k == null) return;
              setLabKind(parseInt(k));
              setLabKindName(labKinds[k.toString()]);
            }}
          >
            <Dropdown.Item key={`lab-1`} eventKey={1}>
              寄生蟲
            </Dropdown.Item>
            <Dropdown.Item key={`lab-2`} eventKey={2}>
              尿液
            </Dropdown.Item>
          </DropdownButton>
        </Col>
        初查日期：
        <Col sm={2} className="d-flex align-items-center">
          <DateTimePicker
            className="wid-110"
            value={checkDate}
            onChange={(date) => {
              if (date !== null) {
                setCheckDate(date);
              }
            }}
          />
        </Col>
        複查日期：
        <Col sm={2} className="d-flex align-items-center">
          <DateTimePicker
            className="wid-110"
            value={checkDate2}
            onChange={(date) => {
              console.log('onChange',date)
              if (date !== null) {
                setCheckDate2(date);
              }
            }}
          />
        </Col>
        <Button
          className="ml-auto"
          disabled={btnDisabled}
          onClick={() => checkDuplicate()}
        >
          新增
        </Button>
      </Row>
      <Row className="pt-3 mt-3 mb-3" style={dashBorder}>
        <Col>
          <Button
            className="mr-1"
            disabled={updateStudent}
            onClick={() => onUpdateLabSemResult()}
            >
            更新有受檢且無異狀
          </Button>
          <Button
            className="mr-1"
            disabled={updateStudent}
            onClick={() => onUpdateLabSemResultNoExmined()}
            >
            更新未受檢
          </Button>
          <Button
            className="mr-1"
            disabled={updateStudent}
            onClick={() => onUpdateLabSemResultCheckDate(true, false)}
            >
            更新初查日期
          </Button>
          <Button
            className="mr-1"
            disabled={updateStudent}
            onClick={() => onUpdateLabSemResultCheckDate(false, true)}
            >
            更新複查日期
          </Button>
        </Col>
        <Col sm={4}>
          根據上方表格，將更新已勾選班級的學生健康資料
        </Col>
      </Row>
      {/* <Row className="mb-2 row">
        <Col sm={2}>
          <FormCheck
            className="ml-3"
            ref={showLabSemNow}
            label="只顯示現在學年"
            onChange={(e) => getLabSemList()}
          />
        </Col>
      </Row> */}
      <Row>
        <EditableTableDiv
          fixCol={3}
          editable
          deleteable
          headers={modalDisplayHeader}
          values={labSettingData}
          onEdit={onEditMetric}
          onDelete={onDeleteMetric}
          onSelected={(s) => {
            setSelectedIndex((last) => {
              if (s.index !== undefined) {
                if (s.checked) last.add(s.index);
                else last.delete(s.index);
                return new Set(last);
              } else {
                if (s.checked) return new Set(labSemList.keys());
                else return new Set();
              }
            });
          }}
        />
      </Row>
      <Modal
        show={editing}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton onHide={onHideEditing}>
          <Modal.Title id="contained-modal-title-vcenter">
            實驗室檢查設定-{' '}
            {editingStudent?.labKind ? labKinds[editingStudent?.labKind] : ''}
          </Modal.Title>
        </Modal.Header>
        <Form
          initialValues={editingStudent}
          onSubmit={onUpdateLabSemExamDate}
          validateOnBlur
          subscription={{ submitting: true, pristine: true }}
          render={(prop) => {
            const { values, handleSubmit } = prop;
            const rowClassName =
              'pb-3 align-items-center justify-content-start';
            return (
              <React.Fragment>
                <Modal.Body className="text-center">
                  <Container>
                    <Row className={rowClassName}>
                      {modalInputFieldTitle('受檢學年')}
                      <Col sm={3}>
                        ({editingStudent?.year}){I18N.Grades[1].substring(0, 1)}
                      </Col>
                      {modalInputFieldTitle('檢查年級')}
                      <Col sm={3}>{editingStudent?.gradeID}</Col>
                    </Row>
                    <Row className={rowClassName}>
                      {modalInputFieldTitle('檢查種類')}
                      <Col sm={3}>
                        {editingStudent?.labKind
                          ? labKinds[editingStudent?.labKind]
                          : ''}
                      </Col>
                      {modalInputFieldTitle('檢查班級')}
                      <Col sm={3}>{editingStudent?.no}</Col>
                    </Row>
                    <Row className={rowClassName}>
                      {modalInputFieldTitle('初查日期')}
                      <Col sm={3}>
                        <DateTimeField property="checkDate" />
                      </Col>
                      {modalInputFieldTitle('複查日期')}
                      <Col sm={3}>
                        <DateTimeField property="checkDate2" />
                      </Col>
                    </Row>
                    {/* <Row className={rowClassName}>
                      <Col className="text-left">
                        <FormCheck
                          ref={checkDateInputRef}
                          className="text-primary"
                          label="更新檢查日期到學生實驗室檢查資料"
                        />
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col className="text-left">
                        <FormCheck
                          ref={checkDate2InputRef}
                          className="text-success"
                          label="更新複查日期到學生實驗室檢查資料"
                        />
                      </Col>
                    </Row> */}
                  </Container>
                </Modal.Body>
                <Modal.Footer>
                  <Button type="button" variant="warning" onClick={clearCheckDate2}>
                    清空複查日
                  </Button>
                  <Button type="submit" onClick={handleSubmit}>
                    儲存
                  </Button>
                  <Button
                    type="reset"
                    variant="secondary"
                    onClick={onHideEditing}
                  >
                    關閉
                  </Button>
                </Modal.Footer>
              </React.Fragment>
            );
          }}
        />
      </Modal>
    </AuthedLayout>
  );
};

export const LabSemPage = connector(labSemPage);